import React from "react";
import axios from "axios";
import { Star, X } from "lucide-react";

const API = window.location.origin;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem("auth_token")}` });

export const SOURCE_META = {
  gyg:         { label: "GetYourGuide", short: "GYG", color: "#FF5533" },
  viator:      { label: "Viator",       short: "Viator", color: "#1D8A5E" },
  tripadvisor: { label: "TripAdvisor",  short: "TripAdvisor", color: "#34E0A1", text: "#0A2E24" },
  google:      { label: "Google",       short: "Google", color: "#4285F4" },
  manual:      { label: "Manuel",       short: "Manuel", color: "#64748B" },
};

export const sourceMeta = (s) => SOURCE_META[s] || { label: s || "-", short: s || "-", color: "#6B7280", };

export const reviewsApi = {
  list: (params) => axios.get(`${API}/api/reviews`, { headers: headers(), params }).then(r => r.data),
  stats: () => axios.get(`${API}/api/reviews/stats`, { headers: headers() }).then(r => r.data),
  sources: () => axios.get(`${API}/api/reviews/sources`, { headers: headers() }).then(r => r.data),
  places: () => axios.get(`${API}/api/reviews/places`, { headers: headers() }).then(r => r.data),
  monthly: () => axios.get(`${API}/api/reviews/stats/monthly`, { headers: headers() }).then(r => r.data.rows || []),
  create: (body) => axios.post(`${API}/api/reviews`, body, { headers: headers() }).then(r => r.data),
  remove: (id) => axios.delete(`${API}/api/reviews/${id}`, { headers: headers() }).then(r => r.data),
  match: (id, body) => axios.post(`${API}/api/reviews/${id}/match`, body, { headers: headers() }).then(r => r.data),
  unmatch: (id) => axios.delete(`${API}/api/reviews/${id}/match`, { headers: headers() }).then(r => r.data),
  rematch: () => axios.post(`${API}/api/reviews/rematch`, {}, { headers: headers() }).then(r => r.data),
  bookingSearch: (q, opts = {}) => axios.get(`${API}/api/reviews/booking-search`, { headers: headers(), params: { q, ...opts } }).then(r => r.data),
  importPreview: (file) => {
    const fd = new FormData(); fd.append("file", file);
    return axios.post(`${API}/api/reviews/import/preview`, fd, { headers: headers() }).then(r => r.data);
  },
  importFile: (file, source, mapping) => {
    const fd = new FormData(); fd.append("file", file); fd.append("source", source); fd.append("mapping", JSON.stringify(mapping));
    return axios.post(`${API}/api/reviews/import`, fd, { headers: headers() }).then(r => r.data);
  },
  garsonStats: (params) => axios.get(`${API}/api/reviews/stats/garson`, { headers: headers(), params }).then(r => r.data),
  sourceTrend: (days = 30) => axios.get(`${API}/api/reviews/stats/source-trend`, { headers: headers(), params: { days } }).then(r => r.data),
  boats: () => axios.get(`${API}/api/boats`, { headers: headers() }).then(r => r.data).catch(() => null),
  assign: (id, body) => axios.post(`${API}/api/reviews/${id}/assign`, body, { headers: headers() }).then(r => r.data),
  assignGarson: (id, garson) => axios.post(`${API}/api/reviews/${id}/assign-garson`, { garson }, { headers: headers() }).then(r => r.data),
  unassignGarson: (id) => axios.delete(`${API}/api/reviews/${id}/assign-garson`, { headers: headers() }).then(r => r.data),
  aliases: () => axios.get(`${API}/api/reviews/garson-aliases`, { headers: headers() }).then(r => r.data),
  setAlias: (raw, canonical) => axios.post(`${API}/api/reviews/garson-aliases`, { raw, canonical }, { headers: headers() }).then(r => r.data),
  rebuildAliases: () => axios.post(`${API}/api/reviews/garson-aliases/rebuild`, {}, { headers: headers() }).then(r => r.data),
  aliasSuggestions: () => axios.get(`${API}/api/reviews/garson-aliases/suggestions`, { headers: headers() }).then(r => r.data),
  download: async (path, params, fallbackName) => {
    const res = await axios.get(`${API}${path}`, { headers: headers(), params, responseType: "blob" });
    const cd = res.headers["content-disposition"] || "";
    const m = /filename="?([^";]+)"?/.exec(cd);
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = (m && m[1]) || fallbackName; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  },
  garsonlar: () => axios.get(`${API}/api/garsonlar`, { headers: headers() }).then(r => r.data).catch(() => null),
  gygConnect: () => axios.post(`${API}/api/reviews/sources/gyg/connect`, {}, { headers: headers(), timeout: 180000 }).then(r => r.data),
  gygSync: (mode) => axios.post(`${API}/api/reviews/sources/gyg/sync`, {}, { headers: headers(), params: { mode } }).then(r => r.data),
  setSourceUrls: (source, urls) => axios.post(`${API}/api/reviews/sources/${source}/urls`, { urls }, { headers: headers() }).then(r => r.data),
  sourceSync: (source, mode) => axios.post(`${API}/api/reviews/sources/${source}/sync`, {}, { headers: headers(), params: { mode } }).then(r => r.data),
  googleSync: (mode, max_reviews) => axios.post(`${API}/api/reviews/sources/google/sync`, {}, { headers: headers(), params: { mode, ...(max_reviews ? { max_reviews } : {}) } }).then(r => r.data),
  suggest: (days) => axios.post(`${API}/api/reviews/suggest`, {}, { headers: headers(), params: { days } }).then(r => r.data),
  rejectSuggestion: (id) => axios.delete(`${API}/api/reviews/${id}/suggestion`, { headers: headers() }).then(r => r.data),
  acceptAllSuggestions: () => axios.post(`${API}/api/reviews/suggestions/accept-all`, {}, { headers: headers() }).then(r => r.data),
  acceptAllStatus: () => axios.get(`${API}/api/reviews/suggestions/accept-all/status`, { headers: headers() }).then(r => r.data),
  scanMismatch: () => axios.post(`${API}/api/reviews/garson-mismatch/scan`, {}, { headers: headers() }).then(r => r.data),
  scanMismatchStatus: () => axios.get(`${API}/api/reviews/garson-mismatch/scan/status`, { headers: headers() }).then(r => r.data),
  autoAssignGarson: () => axios.post(`${API}/api/reviews/auto-assign-garson/scan`, {}, { headers: headers() }).then(r => r.data),
  autoAssignStatus: () => axios.get(`${API}/api/reviews/auto-assign-garson/status`, { headers: headers() }).then(r => r.data),
  creditGarson: (id, garson) => axios.post(`${API}/api/reviews/${id}/credit-garson`, { garson }, { headers: headers() }).then(r => r.data),
  uncreditGarson: (id) => axios.delete(`${API}/api/reviews/${id}/credit-garson`, { headers: headers() }).then(r => r.data),
  rejectCreditSuggestion: (id) => axios.delete(`${API}/api/reviews/${id}/credit-suggestion`, { headers: headers() }).then(r => r.data),
  acceptAllCredit: () => axios.post(`${API}/api/reviews/credit-suggestions/accept-all`, {}, { headers: headers() }).then(r => r.data),
  acceptAllCreditStatus: () => axios.get(`${API}/api/reviews/credit-suggestions/accept-all/status`, { headers: headers() }).then(r => r.data),
};

export const DISPUTE_REASONS = ["Sahte / rakip yorum", "Rezervasyon bulunamadı", "Yönerge ihlali (hakaret / kişisel bilgi)", "Yanlış işletme / ürün", "Çıkar çatışması", "Gerçeğe aykırı iddia", "Diğer"];

export const DISPUTE_STATUS = {
  pending: { label: "Bekliyor", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  rejected: { label: "Reddedildi", cls: "bg-rose-100 text-rose-800 border-rose-300" },
  removed: { label: "Kaldırıldı", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
};

const D = `${API}/api/review-disputes`;
export const disputesApi = {
  stats: () => axios.get(`${D}/stats`, { headers: headers() }).then(r => r.data),
  list: (params) => axios.get(D, { headers: headers(), params }).then(r => r.data.items || []),
  detail: (id) => axios.get(`${D}/review/${id}`, { headers: headers() }).then(r => r.data),
  addDispute: (id, body) => axios.post(`${D}/review/${id}/disputes`, body, { headers: headers() }).then(r => r.data),
  respond: (did, body) => axios.post(`${D}/disputes/${did}/response`, body, { headers: headers() }).then(r => r.data),
  addEvent: (id, body) => axios.post(`${D}/review/${id}/events`, body, { headers: headers() }).then(r => r.data),
  setRemoved: (id, removed) => axios.post(`${D}/review/${id}/removed`, { removed }, { headers: headers() }).then(r => r.data),
  deleteEvent: (eid) => axios.delete(`${D}/events/${eid}`, { headers: headers() }).then(r => r.data),
  deleteDispute: (did) => axios.delete(`${D}/disputes/${did}`, { headers: headers() }).then(r => r.data),
  templates: () => axios.get(`${D}/templates`, { headers: headers() }).then(r => r.data.templates || []),
  addTemplate: (body) => axios.post(`${D}/templates`, body, { headers: headers() }).then(r => r.data),
  deleteTemplate: (tid) => axios.delete(`${D}/templates/${tid}`, { headers: headers() }).then(r => r.data),
};

export function DisputeBadge({ status, count, testId }) {
  const m = DISPUTE_STATUS[status];
  if (!m) return null;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${m.cls}`} data-testid={testId || "dispute-badge"}>
      {count > 0 && status !== "removed" ? `${count}. itiraz · ` : ""}{m.label}
    </span>
  );
}

export function fmtDT(iso) {
  if (!iso) return "-";
  try { return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

export function fmtDate(iso) {
  if (!iso) return "-";
  try { return new Date(iso + (iso.length === 10 ? "T00:00:00" : "")).toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" }); }
  catch { return iso; }
}

export function fmtMonth(iso) {
  if (!iso) return "-";
  try { return new Date(iso.slice(0, 10) + "T00:00:00").toLocaleDateString("tr-TR", { month: "long", year: "numeric" }); } catch { return iso; }
}

export function Stars({ value, size = "text-xs" }) {
  const v = Number(value) || 0;
  const color = v >= 4 ? "text-amber-400" : v === 3 ? "text-orange-400" : "text-red-400";
  return (
    <span className={`inline-flex items-center gap-0.5 ${size} ${color}`} title={`${v} yıldız`} data-testid="review-stars">
      {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-3.5 h-3.5" fill={i <= v ? "currentColor" : "none"} strokeWidth={i <= v ? 0 : 1.5} />)}
    </span>
  );
}

export function SourceBadge({ source }) {
  const m = sourceMeta(source);
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full"
      style={{ backgroundColor: m.color, color: m.text || "#fff" }} data-testid={`source-badge-${source}`}>
      {m.short}
    </span>
  );
}

export const inputCls = "bg-slate-800 border border-slate-700 text-white text-sm rounded-lg px-3 py-1.5 placeholder:text-slate-500 focus:border-blue-500 outline-none";
export const btnCls = "flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50";

export function Modal({ open, onClose, title, children, width = "max-w-xl", testId }) {
  React.useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onMouseDown={onClose} data-testid={testId}>
      <div className={`w-full ${width} bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl`} onMouseDown={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-800">
          <h3 className="text-base font-semibold text-white">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="modal-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}
